import {FC, FormEvent, useEffect} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {Form} from 'react-bootstrap'
import {useAuth} from '../../hooks/useAuth'
import {useTranslation} from 'react-i18next'
import {prefetchRoute} from '../../utils/routePrefetch'
import SocialAuthButtons from '../../components/auth/SocialAuthButtons'
import AuthEmailInput from '@components/ui/AuthEmailInput.tsx'
import AuthPasswordInput from '@components/ui/AuthPasswordInput.tsx'
import UnauthenticatedLayout from '../../components/layout/UnauthenticatedLayout'
import {authStyles} from '../../components/layout/authStyles'
import {useAuthForm} from '../../hooks/useAuthForm'
import {analyticsService} from '../../services/analyticsService'
import {peekPendingSocietyJoin} from '../../utils/pendingSocietyJoin'

const SignupPage: FC = () => {
    const {register, isAuthenticated} = useAuth()
    const navigate = useNavigate()
    const {t} = useTranslation(['auth', 'common'])

    const {
        values,
        touched,
        errors,
        isLoading,
        handleFieldChange,
        handleFieldBlur,
        setLoading,
        isFormValid
    } = useAuthForm(true)

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard', {replace: true})
        } else {
            prefetchRoute('dashboard')
        }
    }, [isAuthenticated, navigate])

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault()
        setLoading(true)
        analyticsService.track('auth.register.attempted')

        // peek, not take: the stash stays for the same-device post-auth redemption; the backend
        // copy covers verification on a different device. Double redemption is idempotent.
        const result = await register({
            email: values.email,
            password: values.password,
            joinCode: peekPendingSocietyJoin() ?? undefined
        })

        if (result.success) {
            analyticsService.track('auth.register.success')
            if (result.requiresVerification) {
                navigate('/verify-email-pending', {
                    replace: true,
                    state: {email: result.email}
                })
            } else {
                navigate('/dashboard', {replace: true})
            }
        } else {
            analyticsService.track('auth.register.failed', {error_type: result.error})
        }

        setLoading(false)
    }

    return (
        <UnauthenticatedLayout>
            <h1 className={authStyles.pageTitle}>{t('auth:sign_up')}</h1>

            <Form onSubmit={handleSubmit} data-testid="signup-form">
                <div className={authStyles.formSection}>
                    <label className={authStyles.inputLabel}>
                        {t('common:fields.email_address')}
                    </label>
                    <AuthEmailInput
                        value={values.email}
                        onChange={handleFieldChange('email')}
                        onBlur={handleFieldBlur('email')}
                        placeholder={t('common:placeholders.enter_email')}
                        testId="email-input"
                    />
                    {touched.email && errors.email && (
                        <div className={authStyles.errorMessage} data-testid="email-input-error">{errors.email}</div>
                    )}
                </div>

                <div className={authStyles.formSection}>
                    <label className={authStyles.inputLabel}>
                        {t('common:fields.password')}
                    </label>
                    <AuthPasswordInput
                        value={values.password}
                        onChange={handleFieldChange('password')}
                        onBlur={handleFieldBlur('password')}
                        placeholder={t('auth:minimum_8_characters')}
                        testId="password-input"
                        autoComplete="new-password"
                        minLength={8}
                    />
                    {touched.password && errors.password && (
                        <div className={authStyles.errorMessage}
                             data-testid="password-input-error">{errors.password}</div>
                    )}
                </div>

                <div className={authStyles.formSection}>
                    <label className={authStyles.inputLabel}>
                        {t('common:fields.confirm_password')}
                    </label>
                    <AuthPasswordInput
                        value={values.confirmPassword}
                        onChange={handleFieldChange('confirmPassword')}
                        onBlur={handleFieldBlur('confirmPassword')}
                        placeholder={t('common:fields.confirm_password')}
                        testId="confirm-password-input"
                        autoComplete="new-password"
                        showSubmitButton
                        submitDisabled={!isFormValid() || isLoading}
                        isLoading={isLoading}
                        submitAriaLabel="Sign up"
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                        <div className={authStyles.errorMessage}
                             data-testid="confirm-password-input-error">{errors.confirmPassword}</div>
                    )}
                </div>
            </Form>

            <div className={authStyles.divider}>
                <div className={authStyles.dividerLine}/>
                <span className={authStyles.dividerText}>{t('auth:or_divider')}</span>
                <div className={authStyles.dividerLine}/>
            </div>

            <SocialAuthButtons/>

            <footer className={authStyles.footer}>
                <span className={authStyles.footerText}>
                    {t('auth:have_account_question')}
                </span>
                <br/>
                <Link to="/login" className={authStyles.footerLink} data-testid="login-link">
                    {t('auth:sign_in_here')}
                </Link>
            </footer>
        </UnauthenticatedLayout>
    )
}

export default SignupPage
